import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { reminderService } from '../services/reminderService';
import { eventService } from '../services/eventService';
import { toast } from 'react-toastify';
import LoadingOverlay from '../components/LoadingOverlay';
import './ReminderForm.css';

const escapeHtml = (text) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const renderPreview = (text) => {
  const escaped = escapeHtml(text);
  return escaped
    .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    .replace(/~(.*?)~/g, '<del>$1</del>')
    .replace(/```(.*?)```/g, '<code>$1</code>')
    .replace(/\n/g, '<br />');
};

const applyFormat = (ref, tokenStart, tokenEnd = tokenStart) => {
  const textarea = ref.current;
  if (!textarea) return;
  const { selectionStart, selectionEnd, value } = textarea;
  const selected = value.substring(selectionStart, selectionEnd) || 'texto';
  const before = value.substring(0, selectionStart);
  const after = value.substring(selectionEnd);
  const formatted = `${tokenStart}${selected}${tokenEnd}`;
  const newValue = `${before}${formatted}${after}`;
  const cursor = before.length + formatted.length;
  textarea.value = newValue;
  textarea.focus();
  textarea.setSelectionRange(cursor, cursor);
  const event = new Event('input', { bubbles: true });
  textarea.dispatchEvent(event);
};

const ReminderForm = () => {
  const { id: eventId, reminderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(reminderId);
  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [loadingReminder, setLoadingReminder] = useState(false);
  const [daysBefore, setDaysBefore] = useState(1);
  const [message, setMessage] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const messageRef = useRef(null);

  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const fileBase = apiBase.replace(/\/api$/, '');

  // Carregar dados de duplicação se fornecidos
  useEffect(() => {
    if (location.state?.duplicate) {
      setDaysBefore(location.state.daysBefore || 1);
      setMessage(location.state.message || '');
      if (location.state.imageUrl) {
        setImagePreview(`${fileBase}${location.state.imageUrl}`);
      }
      toast.info('Lembrete duplicado. Ajuste os dados e salve.');
    }
  }, [location.state, fileBase]);

  useEffect(() => {
    const loadEvent = async () => {
      try {
        const data = await eventService.getById(eventId);
        setEvent(data.event);
      } catch (error) {
        toast.error('Erro ao carregar evento');
        navigate('/dashboard');
      } finally {
        setLoadingEvent(false);
      }
    };

    loadEvent();
  }, [eventId, navigate]);

  useEffect(() => {
    if (!isEdit) return;

    const loadReminder = async () => {
      setLoadingReminder(true);
      try {
        const data = await reminderService.getById(eventId, reminderId);
        const reminder = data.reminder;
        setDaysBefore(reminder.days_before);
        setMessage(reminder.message || '');
        if (reminder.image_url) {
          setImagePreview(`${fileBase}${reminder.image_url}`);
        }
      } catch (error) {
        toast.error('Erro ao carregar lembrete');
        navigate(`/events/${eventId}`);
      } finally {
        setLoadingReminder(false);
      }
    };

    loadReminder();
  }, [eventId, reminderId, isEdit, navigate, fileBase]);

  const onImageChange = (e) => {
    const file = e.target.files?.[0];
    setImage(file || null);
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!daysBefore || Number(daysBefore) < 0) {
      toast.error('Informe os dias de antecedência');
      return;
    }
    if (!message.trim()) {
      toast.error('Digite o conteúdo do lembrete');
      return;
    }

    const formData = new FormData();
    formData.append('days_before', daysBefore);
    formData.append('message', message);
    if (image) formData.append('image', image);

    setSaving(true);
    try {
      if (isEdit) {
        await reminderService.update(eventId, reminderId, formData);
        toast.success('Lembrete atualizado');
      } else {
        await reminderService.create(eventId, formData);
        toast.success('Lembrete criado');
      }
      navigate(`/events/${eventId}`);
    } catch (error) {
      const defaultMsg = isEdit ? 'Erro ao atualizar lembrete' : 'Erro ao criar lembrete';
      toast.error(error.response?.data?.error || defaultMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="reminder-form-page">
      {saving && <LoadingOverlay message="Salvando lembrete..." />}
      <div className="container">
        <div className="reminder-form-header card">
          <div>
            <p className="eyebrow">Lembrete para</p>
            <h1 className="reminder-title">{loadingEvent ? 'Carregando evento...' : event?.title || 'Evento'}</h1>
            {!loadingEvent && event && (
              <p className="event-meta">📅 {new Date(event.start_date).toLocaleDateString()} • 📍 {event.location}</p>
            )}
          </div>
          <div className="reminder-actions">
            <Link to={`/events/${eventId}`} className="btn btn-outline">Voltar</Link>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving || loadingReminder}>
              {isEdit ? 'Atualizar' : 'Salvar'}
            </button>
          </div>
        </div>

        <form className="reminder-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Dias de antecedência*</label>
            <input
              type="number"
              className="form-input"
              min="0"
              value={daysBefore}
              onChange={(e) => setDaysBefore(e.target.value)}
            />
            <small className="hint">Quantos dias antes do evento o lembrete será enviado.</small>
          </div>

          <div className="form-group">
            <label className="form-label">Conteúdo do lembrete*</label>
            <div className="editor-toolbar">
              <button type="button" onClick={() => applyFormat(messageRef, '*')} title="Negrito">B</button>
              <button type="button" onClick={() => applyFormat(messageRef, '_')} title="Itálico">I</button>
              <button type="button" onClick={() => applyFormat(messageRef, '~')} title="Tachado">S</button>
              <button type="button" onClick={() => applyFormat(messageRef, '```')} title="Monoespaçado">{`{ }`}</button>
            </div>
            <textarea
              ref={messageRef}
              className="form-textarea"
              rows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ex: *Lembrete*: o evento acontece em breve!"
            />
            <small className="hint">Suporte a markdown simples. Será convertido para o formato do WhatsApp (negrito, itálico, tachado, monoespaçado).</small>
          </div>

          <div className="preview-card">
            <div className="preview-header">Pré-visualização</div>
            <div className="preview-body" dangerouslySetInnerHTML={{ __html: renderPreview(message || 'Digite o conteúdo do lembrete para ver aqui.') }} />
          </div>

          <div className="form-group">
            <label className="form-label">Imagem (opcional)</label>
            <input type="file" accept="image/*" onChange={onImageChange} />
            {imagePreview && (
              <div className="image-preview">
                <img src={imagePreview} alt="Pré-visualização" />
              </div>
            )}
          </div>

          <div style={{ marginTop: '1rem' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar lembrete'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReminderForm;
